import useDimensionsAccessLevel from '~/app/shared/dimensions/hooks/useDimensionsAccessLevel';
import useDimensions, { DimensionsQueryResponse } from '~/app/shared/dimensions/hooks/useDimensions';
import { Dimension, DimensionsAccess } from '~/app/shared/dimensions/types';
import { DimensionData, DistributionTemplateDistribution } from '~/types/internal-api';
import { DimensionFormData, FormDistributionsValues } from '../draft-entries/util/distributionTypes';

/**
 * Hard-coded ID's of dimension that are mapped to the database.
 */
export type MultipleDimensionId = 1 | 2 | 3;

interface JournalEntryDimensions extends Omit<DimensionsQueryResponse, 'data'> {
    dimensions: Dimension[];
    getById: (id: MultipleDimensionId) => Dimension | undefined;
    getNameByTextId: (textId: string) => string | null;
    convertToPostData: (data: DimensionFormData) => DimensionData[];
    convertToFormData: (data: DimensionData[]) => DimensionFormData | undefined;
    mapDistributions: (formDistributionsValues: FormDistributionsValues[]) => DistributionTemplateDistribution[];
    /**
     * Returns true if the dimension can be shown in the journal.
     * @param dimensionId Id of the dimension to check.
     */
    canShowDimension: (dimensionId: MultipleDimensionId) => boolean;
}

interface DimensionsQueryProps {
    includeDistributionKeys?: boolean;
    includeBarredLines?: boolean;
    includeBarredDimensions?: boolean;
    initialData?: Dimension[];
    skip?: boolean;
}

/**
 * Contains integration of multi-dimension to the new journal.
 */
export default function useJournalEntryDimensions(props: DimensionsQueryProps = {}): JournalEntryDimensions {
    const hasDimensionsModule = useDimensionsAccessLevel(DimensionsAccess.HasDimension);

    const { data: dimensions, ...dimensionsQuery } = useDimensions(props);

    function getNameByTextId(textId: string): string | null {
        let dimensionId: MultipleDimensionId | null;
        switch (textId?.toUpperCase()) {
            case 'LEDGER_TABORDER_MULTI_DIMENSIONS_1':
                dimensionId = 1;
                break;
            case 'LEDGER_TABORDER_MULTI_DIMENSIONS_2':
                dimensionId = 2;
                break;
            case 'LEDGER_TABORDER_MULTI_DIMENSIONS_3':
                dimensionId = 3;
                break;
            default:
                dimensionId = null;
        }

        return dimensions.find(d => d.id === dimensionId)?.name ?? null;
    }

    function getById(id: MultipleDimensionId): Dimension | undefined {
        return dimensions.find(d => d.id === id);
    }

    function convertToFormData(data: DimensionData[]): DimensionFormData | undefined {
        let formData: DimensionFormData = {};
        data.forEach(dimension => {
            if (dimension.dimensionId === 1) {
                formData = { ...formData, dimension1: dimension.dimensionKey };
            }
            if (dimension.dimensionId === 2) {
                formData = { ...formData, dimension2: dimension.dimensionKey };
            }
            if (dimension.dimensionId === 3) {
                formData = { ...formData, dimension3: dimension.dimensionKey };
            }
        });

        return formData;
    }

    function convertToPostData(data: DimensionFormData | undefined): DimensionData[] {
        const postData: DimensionData[] = [];

        if (data?.dimension1) {
            postData.push({
                dimensionId: 1,
                dimensionKey: data.dimension1,
            });
        }

        if (data?.dimension2) {
            postData.push({
                dimensionId: 2,
                dimensionKey: data.dimension2,
            });
        }

        if (data?.dimension3) {
            postData.push({
                dimensionId: 3,
                dimensionKey: data.dimension3,
            });
        }

        return postData;
    }

    function mapDistributionWithNewDimensions(value: FormDistributionsValues): DistributionTemplateDistribution {
        const dimensionData = convertToPostData(value.dimensionData);
        return {
            ...value,
            dimensionData: dimensionData.length > 0 ? dimensionData : undefined,
        };
    }

    function mapDistributions(formDistributionsValues: FormDistributionsValues[]) {
        return (
            formDistributionsValues
                .reduce((acc, value) => {
                    if (value.dimensionData) {
                        return [...acc, mapDistributionWithNewDimensions(value)];
                    }

                    return [...acc, value] as DistributionTemplateDistribution[];
                }, [] as DistributionTemplateDistribution[])
                // Clear department and quantities if agreement has no access to dimensions module
                .map(d =>
                    hasDimensionsModule
                        ? d
                        : {
                              ...d,
                              quantity1: undefined,
                              quantity2: undefined,
                              departmentNumber: undefined,
                              dimensionData: undefined,
                          }
                )
        );
    }

    return {
        ...dimensionsQuery,
        dimensions,
        getById,
        getNameByTextId,
        convertToPostData,
        convertToFormData,
        mapDistributions,
        canShowDimension: (dimensionId: MultipleDimensionId) => getById(dimensionId) !== undefined,
    };
}
